import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import Api from '../../services/api';
import AuthService from '../../services/auth';
import { LoginCredentials, LoginResult } from '../../types/api.types';

interface AuthState {
  isLoggedIn: boolean;
  userData: LoginResult | null;
  fetching: boolean;
  error: Error | AxiosError | null;
}

const initialState: AuthState = {
  isLoggedIn: false,
  userData: null,
  fetching: false,
  error: null
};

// Async thunks
export const logIn = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials) => {
    // Development bypass for UI testing
    if (import.meta.env.VITE_BYPASS_LOGIN === 'true' || 
        (credentials.email === 'dev@test.com' && credentials.password === 'test123')) {
      const mockResponse: LoginResult = {
        id: 'dev-user-123',
        auth_token: 'dev-token-' + Date.now(),
        expires_in: 3600,
        tenant: 'uptime',
        roles: ['Operator', 'User']
      };
      
      // Store mock auth data
      await AuthService.setAuth(mockResponse);
      return mockResponse;
    }
    
    const response = await Api.login(credentials);
    return response;
  }
);

export const logOut = createAsyncThunk(
  'auth/logout',
  async () => {
    await Api.logout();
  }
);

export const checkSession = createAsyncThunk(
  'auth/checkSession',
  async () => {
    const isAuthenticated = await AuthService.isAuthenticated();
    if (!isAuthenticated) {
      throw new Error('Not authenticated');
    }
    
    const userData = await AuthService.getUserData();
    const version = await Api.checkVersion();
    
    return { userData, version };
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<LoginResult>) => {
      state.isLoggedIn = true;
      state.userData = action.payload;
      state.error = null;
    },
    clearAuth: (state) => {
      state.isLoggedIn = false;
      state.userData = null;
      state.error = null;
    },
    setError: (state, action: PayloadAction<Error | AxiosError>) => {
      state.error = action.payload;
      state.fetching = false;
    }
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(logIn.pending, (state) => {
      state.fetching = true;
      state.error = null;
    });
    builder.addCase(logIn.fulfilled, (state, action) => {
      state.isLoggedIn = true;
      state.userData = action.payload;
      state.fetching = false;
      state.error = null;
    });
    builder.addCase(logIn.rejected, (state, action) => {
      state.fetching = false;
      state.error = action.error as Error;
      state.isLoggedIn = false;
    });
    
    // Logout
    builder.addCase(logOut.pending, (state) => {
      state.fetching = true;
    });
    builder.addCase(logOut.fulfilled, (state) => {
      state.isLoggedIn = false;
      state.userData = null;
      state.fetching = false;
      state.error = null;
    });
    builder.addCase(logOut.rejected, (state, action) => {
      state.fetching = false;
      state.error = action.error as Error;
      // Still clear auth state even if logout API fails
      state.isLoggedIn = false;
      state.userData = null;
    });
    
    // Check session
    builder.addCase(checkSession.pending, (state) => {
      state.fetching = true;
    });
    builder.addCase(checkSession.fulfilled, (state, action) => {
      state.isLoggedIn = true;
      state.userData = action.payload.userData;
      state.fetching = false;
      state.error = null;
    });
    builder.addCase(checkSession.rejected, (state) => {
      state.fetching = false;
      state.isLoggedIn = false;
      state.userData = null;
    });
  }
});

export const { setAuthenticated, clearAuth, setError } = authSlice.actions;
export default authSlice.reducer;